import TextInputValidator from '../conventionFields/TextInputValidator';
import { buildEmptyRow, buildEmptyPinnedRow } from './buildEmptyRow';
import {
  SINGLE_SELECT, MULTI_SELECT, PARENT_FIELD, TEXT, CONSTANT, FORMAT_CONTROLLED,
} from './constants';

const SELECT_TYPES = [SINGLE_SELECT, MULTI_SELECT, PARENT_FIELD];
// Derived from the convention: never stored, always recomputed.
const DERIVED_TYPES = [CONSTANT, FORMAT_CONTROLLED];

const itemValue = (item) => (item && typeof item === 'object' ? item.value : item);
const isBlankItem = (item) => {
  const value = itemValue(item);
  return value === undefined || value === null || value === '';
};
const nonBlankCount = (stored) => {
  if (Array.isArray(stored)) return stored.filter((item) => !isBlankItem(item)).length;
  return isBlankItem(stored) ? 0 : 1;
};

// Returns a predicate telling whether a stored (non-blank) item is still valid
// for the column as the convention defines it now.
const buildItemCheck = (column) => {
  if (SELECT_TYPES.includes(column.column_type)) {
    if (column.allow_create && column.column_type !== PARENT_FIELD) return () => true;
    const allowed = new Set((column.values || []).map((option) => option.value));
    return (item) => allowed.has(itemValue(item));
  }
  if (column.column_type === TEXT) {
    const validator = new TextInputValidator(
      column.text_limit,
      column.allowed_characters,
      column.prohibited_characters,
    );
    return (item) => validator.isValid(itemValue(item));
  }
  return () => true;
};

// Mirrors columnDefinitions.js: only combo-capable text/single-select columns
// (and multi-select columns) hold arrays; in the combination row only.
const holdsArray = (column, isPinned) => column.column_type === MULTI_SELECT
  || (isPinned && Boolean(column.allow_combo_generate)
    && (column.column_type === TEXT || column.column_type === SINGLE_SELECT));

const reconcileCell = (column, stored, fallback, isValid, isPinned) => {
  if (DERIVED_TYPES.includes(column.column_type) || stored === undefined) {
    return { value: fallback, dropped: 0 };
  }
  // A stored value of the other shape (a field switched between parent and
  // combo since the snapshot) is stale.
  if (Array.isArray(stored) !== holdsArray(column, isPinned)) {
    return { value: fallback, dropped: nonBlankCount(stored) };
  }
  if (Array.isArray(stored)) {
    const kept = stored.filter((item) => isBlankItem(item) || isValid(item));
    return { value: kept, dropped: stored.length - kept.length };
  }
  if (isBlankItem(stored) || isValid(stored)) return { value: stored, dropped: 0 };
  return { value: fallback, dropped: 1 };
};

/**
 * Fits a stored grid snapshot to the current columns. Cells whose column or
 * value no longer exists fall back to the column's empty value; derived cells
 * (constants, generated values) are always recomputed.
 *
 * @returns {{ rowData: Object[], pinnedRowData: Object, droppedCount: number }}
 */
export const reconcileSnapshot = (snapshot, columns) => {
  const checks = new Map(columns.map((column) => [column.field, buildItemCheck(column)]));
  const storedPinned = snapshot.pinnedRowData || {};
  let droppedCount = 0;

  // Generate copies the combination row's value of every non-combo column into
  // every data row, where it only mirrors it: count a dropped value once, from
  // the pinned row.
  const reconcileRow = (storedRow, baseRow, isPinned) => {
    const countDropped = (field, value, dropped) => {
      const mirrored = !isPinned && JSON.stringify(value) === JSON.stringify(storedPinned[field]);
      if (!mirrored) droppedCount += dropped;
    };
    const stored = storedRow || {};
    const row = { ...baseRow };
    columns.forEach((column) => {
      const { value, dropped } = reconcileCell(
        column,
        stored[column.field],
        baseRow[column.field],
        checks.get(column.field),
        isPinned,
      );
      row[column.field] = value;
      countDropped(column.field, stored[column.field], dropped);
    });
    Object.keys(stored).forEach((field) => {
      if (!checks.has(field)) countDropped(field, stored[field], nonBlankCount(stored[field]));
    });
    return row;
  };

  const rowData = snapshot.rowData
    .map((stored) => reconcileRow(stored, buildEmptyRow(columns), false));
  const pinnedRowData = reconcileRow(snapshot.pinnedRowData, buildEmptyPinnedRow(columns), true);

  return { rowData, pinnedRowData, droppedCount };
};

const derivedFields = (columns) => new Set(
  columns
    .filter((column) => DERIVED_TYPES.includes(column.column_type))
    .map((column) => column.field),
);

const withoutFields = (row, fields) => {
  if (!row) return row;
  return Object.fromEntries(Object.entries(row).filter(([field]) => !fields.has(field)));
};

/** Copies grid data without the convention-derived (constant, generated) cells. */
export const stripDerivedCells = ({ rowData, pinnedRowData }, columns) => {
  const fields = derivedFields(columns);
  return {
    rowData: rowData.map((row) => withoutFields(row, fields)),
    pinnedRowData: withoutFields(pinnedRowData, fields),
  };
};

/** True when any non-derived cell, in any row or the combination row, holds a value. */
export const hasContent = ({ rowData, pinnedRowData }, columns) => {
  const fields = derivedFields(columns);
  const rowHasContent = (row) => Object.keys(row || {})
    .some((field) => !fields.has(field) && nonBlankCount(row[field]) > 0);
  return rowData.some(rowHasContent) || rowHasContent(pinnedRowData);
};
